import React, {ChangeEvent, FC, useEffect, useState} from 'react';
import {
  deleteStructureDepartment,
  getCurrentOrganizationalStructure,
  getCurrentOrganizationalStructureAction, updateDepartmentOffice,
  updateStructureDepartment,
  updateStructurePosition
} from '../../../../shared/services/organizational-structure-service';
import { useDispatch, useSelector } from 'react-redux';
import { StructureDepartmentModel } from '../../../../shared/models/organizational-structure.model';
import { faMinus, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InputSelect from '../../../../shared/components/atoms/input-select';
import {
  handleStructureDepartmentInputSelectChange
} from '../../../../shared/services/organizational-structure-input-change-state';
import { convertToPositiveNumber, handleNumericInput } from '../../../../shared/utils/function-validators';
import { notificationService } from '../../../../shared/services/notification-service';
import DeleteConfirmationModal from '../../../../shared/components/molecules/modals/delete-confirmation-modal';
import { convertToCurrency } from '../../../../shared/utils/currency';
import InputCurrency from '../../../../shared/components/inputs/input-currency';
import { Dropdown, SelectDropdown } from '../../../../shared/models/common.model';
import HeaderEmployeeSearch from '../inputs/header-employee-search';
import { StructureDepartmentOfficeTypeOptions } from '../../../../shared/utils/constants';

interface StructureDepartmentFormProps extends StructureDepartmentModel {
  index: number;
  lastButton: boolean;
}

const StructureDepartmentForm: FC<StructureDepartmentFormProps> = ({
                                                                     index,
                                                                     id,
                                                                     department_office_id,
                                                                     department_office_index,
                                                                     division_group_office_id,
                                                                     division_group_office_index,
                                                                     director_id,
                                                                     director_index,
                                                                     deputy_director_id,
                                                                     deputy_director_index,
                                                                     type,
                                                                     name,
                                                                     power_rate,
                                                                     lastButton,
                                                                     is_acting_in_position,
                                                                     is_empty,
                                                                     user_id,
                                                                     pay_account_number,
                                                                     level,
                                                                   }) => {
  const dispatch = useDispatch();
  const organizationalStructure = useSelector(getCurrentOrganizationalStructure);
  const organizationalStructureAction = useSelector(getCurrentOrganizationalStructureAction);
  const disable = organizationalStructureAction.status === 'view';
  const [showModal, setShowModal] = useState(false);

  const handleDelete = () => {
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    organizationalStructure.structure_positions.forEach((data, dataIndex) => {
      if (data.structure_department_index === index) {
        const resetId = {
          target: {
            name: 'structure_department_id',
            value: null,
          }
        };
        const resetIndex = {
          target: {
            name: 'structure_department_index',
            value: null,
          }
        };
        updateStructurePosition(dataIndex, resetId, dispatch);
        updateStructurePosition(dataIndex, resetIndex, dispatch);
      } else if ((data.structure_department_index || -1) > index) {
        const resetIndex = {
          target: {
            name: 'structure_department_index',
            value: (data.structure_department_index as number) - 1,
          }
        };
        updateStructurePosition(dataIndex, resetIndex, dispatch);
      }
    });
    deleteStructureDepartment({index: index}, dispatch);
    setShowModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const displayModal = () => {
    if (showModal) {
      const child = organizationalStructure.structure_positions
        .filter(data => data.structure_department_index === index);
      const message = <>
        <p>คุณแน่ใจหรือไม่ว่าต้องการลบข้อมูลนี้</p>
        {
          child.length > 0 ?
            <p className="m-0">
              มี ตำแหน่ง ภายใต้ แผนก <b>{name}</b> ทั้งหมด <b>{child.length}</b> ตำแหน่ง
            </p> : ''
        }
        {
          child.map((data, index) => {
            return <p key={index} className="m-0">- {data.name} (อัตรากำลัง: {convertToCurrency(data.power_rate)})</p>;
          })
        }
        {
          child.length > 0 ?
            <div className="alert alert-warning mb-0 mt-2" role="alert">
              <FontAwesomeIcon icon={faWarning} className="me-2"/>
              เมื่อกดลบแล้ว ตำแหน่งภายใต้ทั้งหมดจะต้องเลือก แผนก ใหม่
            </div> : ''
        }
      </>;
      return <DeleteConfirmationModal
        show={showModal}
        size={'lg'}
        title={'ยืนยันการลบข้อมูล'}
        description={message}
        onHide={handleCloseModal}
        onConfirm={handleConfirmDelete}
      />;

    }
    return;
  };

  const buttonDisplay = () => {
    return <button className="btn btn-danger" type="button" onClick={handleDelete} hidden={disable || index === 0}>
      <FontAwesomeIcon icon={faMinus}/>
    </button>;
  };

  const getOptions = () => {
    if (type === 'normal') {
      return organizationalStructure.division_group_offices.map((data, index) => {
        const {label, disabled} = getPowerRateRemain(index, data.power_rate || 0, data.is_empty);
        return {
          label: `${data.name || '-'} ${convertToCurrency(label)}`,
          value: index,
          disabled: disabled
        };
      });
    } else if (type === 'director') {
      return organizationalStructure.directors.map((data, index) => {
        return {
          label: `${data.name || '-'}`,
          value: index,
          disabled: false
        };
      })
    } else if (type === 'deputy_director') {
      return organizationalStructure.deputy_directors.map((data, index) => {
        return {
          label: `${data.name || '-'} (${data.pay_account_number})`,
          value: index,
          disabled: false
        };
      })
    } else if (type === 'department_office') {
      return organizationalStructure.department_offices.map((data, index) => {
        const {label, disabled} = getPowerRateDepartmentOfficeRemain(index, data.power_rate || 0, data.is_empty);
        return {
          label: `${data.name || '-'} ${convertToCurrency(label)}`,
          value: index,
          disabled: disabled
        };
      })
    }

    return [];
  };

  const getOptionLabel = () => {
    if (type === 'normal') {
      return 'กอง / กลุ่ม / สำนักงาน';
    } else if (type === 'director') {
      return 'ผู้อำนวยการ';
    } else if (type === 'department_office') {
      return 'สำนัก / ฝ่าย';
    } else {
      return 'รองผู้อำนวยการ';
    }
  }

  const getPowerRateDepartmentOfficeRemain = (department_office_index: number | null, powerRateCap: number, isEmptyInPosition: boolean) => {
    const powerRateUsed = organizationalStructure.structure_departments
      .filter(data => data.department_office_index === department_office_index)
      .map(data => parseInt(String(data.power_rate || 0)))
      .reduce((total_used, power_rate) => {
        return total_used + power_rate;
      }, 0);
    return {
      label: `(อัตรากำลัง: ${convertToCurrency(isEmptyInPosition ? powerRateUsed : powerRateUsed + 1)}/${convertToCurrency(powerRateCap)})`,
      disabled: isEmptyInPosition ? powerRateUsed >= powerRateCap : powerRateUsed + 1 >= powerRateCap
    };
  };

  const getPowerRateRemain = (division_group_office_index: number | null, powerRateCap: number, isEmptyInPosition: boolean) => {
    const powerRateUsed = organizationalStructure.structure_departments
      .filter(data => data.division_group_office_index === division_group_office_index)
      .map(data => {
        let powerRate = parseInt(String(data.power_rate || 0));
      return powerRate;
      })
      .reduce((total_used, power_rate) => {
        return total_used + power_rate;
      }, 0);
    return {
      label: `(อัตรากำลัง: ${convertToCurrency(isEmptyInPosition ? powerRateUsed : powerRateUsed + 1)}/${convertToCurrency(powerRateCap)})`,
      disabled: isEmptyInPosition ? powerRateUsed >= powerRateCap : powerRateUsed + 1 >= powerRateCap
    };
  };

  const validateMaxPowerRate = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value = convertToPositiveNumber(event.target.value);

    if (division_group_office_index !== null) {
      let powerRateCap = organizationalStructure.division_group_offices[division_group_office_index].power_rate || 0;
      if (!organizationalStructure.division_group_offices[division_group_office_index].is_empty) {
        powerRateCap = Math.max(0, powerRateCap - 1);
      }
      const powerRateUse = organizationalStructure.structure_departments
        .filter((data, data_index) => data.division_group_office_index === division_group_office_index && data_index !== index)
        .map(data => data.power_rate || 0)
        .map(Number)
        .reduce((total_used, power_rate) => {
          return total_used + power_rate;
        }, 0);
      const powerRateRemain = powerRateCap - powerRateUse;
      if (+event.target.value > powerRateRemain) {
        event.target.value = powerRateRemain.toString();
        notificationService.infoNotification('อัตรากำลังเกินกำหนด', `ปรับอัตรากำลังให้พอดีกับยอดที่เหลืออยู่คือ ${convertToCurrency(powerRateRemain)}`);
      }
    } else if (department_office_index !== null) {
      const powerRateCap = organizationalStructure.department_offices[department_office_index].power_rate || 0;
      const powerRateUse = organizationalStructure.structure_departments
        .filter((data, data_index) => data.department_office_index === department_office_index && data_index !== index)
        .map(data => data.power_rate || 0)
        .map(Number)
        .reduce((total_used, power_rate) => {
          return total_used + power_rate;
        }, 0);
      const powerRateRemain = powerRateCap - powerRateUse;
      if (+event.target.value > powerRateRemain) {
        event.target.value = powerRateRemain.toString();
        notificationService.infoNotification('อัตรากำลังเกินกำหนด', `ปรับอัตรากำลังให้พอดีกับยอดที่เหลืออยู่คือ ${convertToCurrency(powerRateRemain)}`);
      }
    }
    updateStructureDepartment(index, event, dispatch);
  };

  const handleOnCheckEmpty = (e: ChangeEvent<HTMLInputElement>) => {
    const setIsEmpty = {
      target: {
        name: 'is_empty',
        value: e.target.checked
      }
    };
    // const resetPayAccountNumber = {
    //   target: {
    //     name: 'pay_account_number',
    //     value: e.target.checked ? '-' : null
    //   }
    // };
    updateStructureDepartment(index, setIsEmpty, dispatch);
    // updateStructureDepartment(index, resetPayAccountNumber, dispatch);

    if (e.target.checked) {
      const setUserId = {
        target: {
          name: 'user_id',
          value: null
        }
      };
      updateStructureDepartment(index, setUserId, dispatch);
    }
  };

  const handleOnCheckActingInPosition = (e: ChangeEvent<HTMLInputElement>) => {
    const check = {
      target: {
        name: 'is_acting_in_position',
        value: e.target.checked
      }
    };
    updateStructureDepartment(index, check, dispatch);
  };

  const handleOnSelectEmployee = (value: Dropdown) => {
    const updateData = {
      target: {
        name: 'user_id',
        value: +value.value
      }
    };
    updateStructureDepartment(index, updateData, dispatch);
  };

  const validateNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleNumericInput(e);
    updateStructureDepartment(index, e, dispatch);
  };

  const resetPowerRate = () => {
    const resetPowerRate = {
      target: {
        name: 'power_rate',
        value: null
      }
    }
    updateStructureDepartment(index, resetPowerRate, dispatch);
  }

  const resetDirector = () => {
    const resetDirectorId = {
      target: {
        name: 'director_id',
        value: null
      }
    }
    const resetDirectorIndex = {
      target: {
        name: 'director_index',
        value: null
      }
    }
    updateStructureDepartment(index, resetDirectorId, dispatch);
    updateStructureDepartment(index, resetDirectorIndex, dispatch);
  }

  const resetDepartmentOffice = () => {
    const resetDepartmentOfficeId = {
      target: {
        name: 'department_office_id',
        value: null
      }
    }
    const resetDepartmentOfficeIndex = {
      target: {
        name: 'department_office_index',
        value: null
      }
    }
	updateStructureDepartment(index, resetDepartmentOfficeId, dispatch);
	updateStructureDepartment(index, resetDepartmentOfficeIndex, dispatch);
  }

  const resetDeputyDirector = () => {
    const resetDeputyDirectorId = {
      target: {
        name: 'deputy_director_id',
        value: null
      }
    }
    const resetDeputyDirectorIndex = {
      target: {
        name: 'deputy_director_index',
        value: null
      }
    }
    updateStructureDepartment(index, resetDeputyDirectorId, dispatch);
    updateStructureDepartment(index, resetDeputyDirectorIndex, dispatch);
  }

  const resetDivisionGroupOffice = () => {
    const resetDivisionGroupOfficeId = {
      target: {
        name: 'division_group_office_id',
        value: null
      }
    }
    const resetDivisionGroupOfficeIndex = {
      target: {
        name: 'division_group_office_index',
        value: null
      }
    }
    updateStructureDepartment(index, resetDivisionGroupOfficeId, dispatch);
    updateStructureDepartment(index, resetDivisionGroupOfficeIndex, dispatch);
  }

  const defaultDeputy = () => {
    const defaultDirectorId = {
      target: {
        name: 'director_id',
        value: organizationalStructure.directors[0].id
      }
    };
    const defaultDirectorIndex = {
      target: {
        name: 'director_index',
        value: 0
      }
    };
    updateStructureDepartment(index, defaultDirectorId, dispatch);
    updateStructureDepartment(index, defaultDirectorIndex, dispatch);
  }

  const onTypeChange = (e: SelectDropdown) => {
    if (e.value?.value === 'normal' && type !== 'normal') {
      resetDirector();
      resetDeputyDirector();
      resetDepartmentOffice();
      resetPowerRate();
    } else if (e.value?.value === 'director' && type !== 'director') {
      resetDivisionGroupOffice();
      resetDeputyDirector();
      resetDepartmentOffice();
      defaultDeputy();
      resetPowerRate();
    } else if (e.value?.value === 'deputy_director' && type !== 'deputy_director') {
      resetDirector();
      resetDivisionGroupOffice();
      resetDepartmentOffice();
      resetPowerRate();
    } else if (e.value?.value === 'department_office' && type !== 'department_office') {
      resetDirector();
      resetDeputyDirector();
      resetDivisionGroupOffice();
      resetPowerRate();
    }
    handleStructureDepartmentInputSelectChange(index, e, dispatch);
  }

  const typeDisableForDirector = () => {
    return type === 'director';
  }

  const getSelectValue = () => {
    if (type === 'normal') {
      return division_group_office_index;
    } else if (type === 'director') {
      return director_index;
    }  else if (type === 'department_office') {
      return department_office_index;
    } else {
      return deputy_director_index;
    }
  }

  const getSelectName = () => {
    if (type === 'normal') {
      return 'division_group_office_index';
    } else if (type === 'director') {
      return 'director_index';
    }  else if (type === 'department_office') {
      return 'department_office_index';
    } else {
      return 'deputy_director_index';
    }
  }

  const disablePowerRate = () => {
    if (type === 'normal') {
      return division_group_office_index === null;
    } else if (type === 'director') {
      return director_index === null;
    }  else if (type === 'department_office') {
      return department_office_index === null;
    } else {
      return deputy_director_index === null;
    }
  }
  return (
    <>
      <div className="row mb-3">
        <div className="col-sm-12 col-md-4">
          <label className="form-label required">ประเภท</label>
          <InputSelect options={StructureDepartmentOfficeTypeOptions} name="type"
                       value={type}
                       isClearable={false} isDisable={disable}
                       onChange={onTypeChange}
                       placeholder=""/>
        </div>
        <div className="col-sm-12 col-md-4">
          <label className="form-label required">{getOptionLabel()}</label>
          <InputSelect options={getOptions()} placeholder="กรุณาเลือก" name={getSelectName()}
                       onChange={e => handleStructureDepartmentInputSelectChange(index, e, dispatch)}
                       value={getSelectValue()}
                       isClearable={false} isDisable={disable || typeDisableForDirector()}/>
          <input type="text" className="form-control" name={getSelectName()} hidden={true}
                 value={getSelectValue()?.toString() || ''} required={true} onChange={() => {
          }}/>
          <div className="invalid-feedback">
            กรุณากรอกเลือก{getOptionLabel()}
          </div>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-sm-12 col-md-4">
          <label className="form-label required">เลขบัญชีถือจ่าย</label>
          <input type="text" className="form-control" name="pay_account_number" disabled={disable}
                 placeholder="" value={pay_account_number || ''} required={true}
                 onChange={validateNumber}/>
          <div className="invalid-feedback">
            กรุณากรอกเลขบัญชีถือจ่าย
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <label className="form-label required">ชื่อแผนก</label>
          <input type="text" className="form-control" name="name" disabled={disable}
                 placeholder="" value={name || ''} required={true}
                 onChange={(e) => updateStructureDepartment(index, e, dispatch)}/>
          <div className="invalid-feedback">
            กรุณากรอกชื่อแผนก
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <label className="form-label required">อัตรากำลัง</label>
          <div className="input-group">
            <InputCurrency value={power_rate || ''} onChange={validateMaxPowerRate}
                           disable={disable || disablePowerRate()}
                           name="power_rate" required={true}/>
            <div className="input-group-append ps-2">
              {buttonDisplay()}
            </div>
            <div className="invalid-feedback">
              กรุณากรอกอัตรากำลัง
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-sm-12 col-md-4">
          <label className="form-label required">ชื่อหัวหน้าแผนก</label>
          <div className="input-group">
            {
              is_empty ?
                <input type="text" className="form-control" name="name" disabled={true}
                       placeholder="" value={'ตำแหน่งว่างอยู่'} required={true}/>
                :
                <div className="w-100">
                  <HeaderEmployeeSearch onChange={(value) => handleOnSelectEmployee(value)} value={user_id}
                                        changeValue={organizationalStructure.structure_departments.map(data => data.user_id).join(',')}/>
                </div>
            }
            <input type="text" className="form-control" name="user_id" hidden={true} required={true}
                   value={user_id || ''} onChange={() => {
            }}/>
            {
              !is_empty ?
                <div className="invalid-feedback">
                  กรุณากรอกชื่อ ผู้อำนวยการแผนก
                </div>
                : null
            }
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <label className="form-label required">ระดับ</label>
          <div className="input-group">
            <input type="text" className="form-control" name="level" disabled={disable}
                   onChange={(e) => updateStructureDepartment(index, e, dispatch)}
                   placeholder="" value={level || ''} required={true}/>
            <div className="invalid-feedback">
              กรุณากรอกระดับ
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-3 mb-3">
        <div className="col-sm-12 col-md-2">
          <div className="form-check">
            <input className="form-check-input pointer no-validate" type="checkbox" defaultChecked={is_empty}
                   name={`is_empty_${index}`}
                   disabled={disable}
                   onChange={handleOnCheckEmpty}
                   id={`is_empty_${index}`}/>
            <label className="form-check-label pointer" htmlFor={`is_empty_${index}`}>
              ตำแหน่งว่างอยู่ตอนนี้
            </label>
          </div>
        </div>
        <div className="col-sm-12 col-md-2">
          <div className="form-check">
            <input className="form-check-input pointer no-validate" type="checkbox"
                   defaultChecked={is_acting_in_position}
                   name={`is_acting_in_position${index}`}
                   disabled={disable}
                   onChange={handleOnCheckActingInPosition}
                   id={`is_acting_in_position${index}`}/>
            <label className="form-check-label pointer" htmlFor={`is_acting_in_position${index}`}>
              รักษาการในตำแหน่ง
            </label>
          </div>
        </div>
      </div>
      {displayModal()}
      {index === organizationalStructure.structure_departments.length - 1 ? null : <hr/>}
    </>
  );
};

export default StructureDepartmentForm;